import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Users,
  CheckCircle2,
  XCircle,
  Calendar,
  Clock,
  UserPlus,
  Activity,
} from 'lucide-react';
import MenteeProfile from '../components/MenteeProfile';
import EndMatchDialog from '../components/EndMatchDialog';

interface MentorDashboardProps {
  metrics: {
    active_count: number;
    completed_count: number;
    ended_count: number;
    total_count: number;
    this_month_count: number;
  };
  active_matches: Array<{
    id: number;
    mentee: {
      id: number;
      first_name: string;
      last_name: string;
      email_address: string;
    };
    matched_at: string;
    is_stale: boolean;
  }>;
  recent_activity: Array<any>;
}

const MetricCard = ({
  title,
  value,
  icon: Icon,
  description,
  color = 'purple',
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
  color?: string;
}) => {
  const gradientColors = {
    primary: 'from-primary to-primary/80',
    green: 'from-green-500 to-green-600',
    blue: 'from-blue-500 to-blue-600',
    red: 'from-red-500 to-red-600',
    indigo: 'from-indigo-500 to-indigo-600',
  };

  const iconColors = {
    primary: 'text-primary',
    green: 'text-green-500',
    blue: 'text-blue-500',
    red: 'text-red-500',
    indigo: 'text-indigo-500',
  };

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
              {title}
            </p>
            <p className="text-4xl font-light">{value}</p>
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
            <div
              className={`h-1 w-12 bg-gradient-to-r ${gradientColors[color as keyof typeof gradientColors]} rounded-full`}
            />
          </div>
          <Icon
            className={`h-5 w-5 ${iconColors[color as keyof typeof iconColors]} mt-1`}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const ActiveMenteeCard = ({
  match,
}: {
  match: MentorDashboardProps['active_matches'][0];
}) => {
  const [showProfile, setShowProfile] = useState(false);
  const [endMatchOpen, setEndMatchOpen] = useState(false);
  const daysSinceMatch = Math.floor(
    (new Date().getTime() - new Date(match.matched_at).getTime()) /
    (1000 * 60 * 60 * 24)
  );
  const initials = `${match.mentee.first_name?.[0] || ''}${match.mentee.last_name?.[0] || ''
    }`.toUpperCase();

  return (
    <>
      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
        <CardContent className="p-0 flex flex-col flex-1">
          <div className="px-4 pt-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-11 w-11">
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                  {initials || 'M'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-medium text-gray-900 leading-tight truncate">
                  {match.mentee.first_name} {match.mentee.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {match.mentee.email_address}
                </p>
              </div>
              {match.is_stale ? (
                <Badge className="bg-amber-50 text-amber-700 border-transparent">
                  Check-in
                </Badge>
              ) : (
                <Badge className="bg-green-50 text-green-700 border-transparent">
                  Active
                </Badge>
              )}
            </div>

            <div className="flex flex-col gap-2 my-4">
              <div className='flex items-center gap-1.5 text-xs text-gray-500'>
                <Calendar className="h-3.5 w-3.5" />
                Matched {daysSinceMatch} {daysSinceMatch === 1 ? 'day' : 'days'} ago
              </div>
              {match.is_stale && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                  <Clock className="h-3.5 w-3.5" />
                  No check-in in 3+ months
                </div>
              )}
            </div>

          </div>

          <div className="border-t border-gray-100 mx-4 mt-auto" />

          <div className="flex gap-2 items-center px-4 pt-4">
            {match.is_stale ? (
              <Button
                variant='default'
                size='sm'
                onClick={() => router.patch(`/matches/${match.id}/confirm_active`, {}, { preserveScroll: true })}
              >
                Confirm Active
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowProfile(true)}
                >
                  View Application
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link href={`/matches/${match.id}/mentorship_sessions`}>
                    Sessions
                  </Link>
                </Button>
              </div>
            )}

            <Button variant={'outline'} size={'sm'} onClick={() => setEndMatchOpen(true)}>
              End Match
            </Button>
          </div>
        </CardContent>
      </Card>
      {showProfile && (
        <MenteeProfile
          menteeId={match.mentee.id}
          onClose={() => setShowProfile(false)}
          isMatched={true}
        />
      )}
      <EndMatchDialog
        matchId={match.id}
        menteeName={`${match.mentee.first_name} ${match.mentee.last_name}`}
        open={endMatchOpen}
        onOpenChange={setEndMatchOpen}
      />
    </>
  );
};

const EmptyState = () => (
  <Card className="border-0 shadow-sm">
    <CardContent className="flex flex-col items-center justify-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
        <Users className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 mb-2">
        No Active Mentees
      </h3>
      <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
        You don't have any active mentees at the moment. Browse available
        mentees to start making a difference!
      </p>
      <Button asChild>
        <Link href="/matches">
          <UserPlus className="h-4 w-4 mr-2" />
          Browse Mentees
        </Link>
      </Button>
    </CardContent>
  </Card>
);

export default function MentorDashboard({
  metrics,
  active_matches,
  recent_activity,
}: MentorDashboardProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mentor Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Welcome back! Here's an overview of your mentorship activities.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5 mb-8">
          <MetricCard
            title="Active Mentees"
            value={metrics.active_count}
            icon={Users}
            description="Currently mentoring"
            color="primary"
          />
          <MetricCard
            title="Completed"
            value={metrics.completed_count}
            icon={CheckCircle2}
            description="Successful mentorships"
            color="green"
          />
          <MetricCard
            title="Ended"
            value={metrics.ended_count}
            icon={XCircle}
            description="Terminated matches"
            color="red"
          />
          <MetricCard
            title="Total Impact"
            value={metrics.total_count}
            icon={Activity}
            description="All-time mentees"
            color="blue"
          />
          <MetricCard
            title="This Month"
            value={metrics.this_month_count}
            icon={Calendar}
            description="New matches"
            color="indigo"
          />
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-medium text-gray-900">
                Active Mentees
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Your current mentorship relationships
              </p>
            </div>
            {active_matches.length > 0 && (
              <Button variant="outline" asChild>
                <Link href="/matches">Browse More Mentees</Link>
              </Button>
            )}
          </div>

          {active_matches.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {active_matches.map((match) => (
                <ActiveMenteeCard key={match.id} match={match} />
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
}
